import React, { useRef, useEffect, useState, useCallback } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { translations } from '../translations/translations';
import useScrollAnimation from '../hooks/useScrollAnimation';
import '../styles/scrollAnimations.css';
import './WastedBudget.css';

const ChannelIcon = ({ type }) => {
  const icons = {
    linkedin: (
      <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
        <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
      </svg>
    ),
    google: (
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="11" cy="11" r="8" />
        <path d="M21 21l-4.35-4.35" />
      </svg>
    ),
    email: (
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="2" y="4" width="20" height="16" rx="2" />
        <path d="M22 7L12 13L2 7" />
      </svg>
    ),
    compass: (
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10" />
        <polygon points="16.24 7.76 14.12 14.12 7.76 16.24 9.88 9.88 16.24 7.76" />
      </svg>
    ),
    database: (
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <ellipse cx="12" cy="5" rx="9" ry="3" />
        <path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3" />
        <path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5" />
      </svg>
    ),
    gear: (
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="3" />
        <path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 012.83-2.83l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z" />
      </svg>
    ),
    pen: (
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 20h9" />
        <path d="M16.5 3.5a2.12 2.12 0 013 3L7 19l-4 1 1-4L16.5 3.5z" />
      </svg>
    ),
    user: (
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" />
        <circle cx="12" cy="7" r="4" />
      </svg>
    ),
    ai: (
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 3l1.9 4.6L18.5 9.5 13.9 11.4 12 16l-1.9-4.6L5.5 9.5 10.1 7.6 12 3z" />
        <path d="M19 14l.8 2 2 .8-2 .8-.8 2-.8-2-2-.8 2-.8z" />
      </svg>
    ),
  };
  return icons[type] || null;
};

const QuoteCard = ({ person, index }) => {
  const cardRef = useRef(null);
  const [progress, setProgress] = useState(0);
  const rafRef = useRef(null);

  useEffect(() => {
    const onScroll = () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(() => {
        if (!cardRef.current) return;
        const rect = cardRef.current.getBoundingClientRect();
        const vh = window.innerHeight;
        const triggerPoint = vh * 0.82;
        const raw = (triggerPoint - rect.top) / (vh * 0.4);
        setProgress(Math.min(1, Math.max(0, raw)));
      });
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => {
      window.removeEventListener('scroll', onScroll);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, []);

  const eased = progress < 0.5
    ? 4 * progress * progress * progress
    : 1 - Math.pow(-2 * progress + 2, 3) / 2;

  const delay = index * 0.18;
  const delayed = Math.min(1, Math.max(0, (progress - delay) / (1 - delay)));
  const delayedEased = delayed < 0.5
    ? 4 * delayed * delayed * delayed
    : 1 - Math.pow(-2 * delayed + 2, 3) / 2;

  const textY = (1 - delayedEased) * 24;
  const fadeY = (1 - delayedEased) * -30;

  const rotations = [-3, 1, 4];
  const offsets = [8, -4, 12];
  const rotate = rotations[index] || 0;
  const shiftY = offsets[index] || 0;

  return (
    <div
      ref={cardRef}
      className={`wb-quote-scene wb-quote-scene--${index}`}
      style={{
        opacity: delayedEased,
        transform: `translateY(${fadeY + shiftY}px) rotate(${rotate}deg)`,
      }}
    >
      <div
        className="wb-scene-content"
        style={{ transform: `translateY(${textY}px)` }}
      >
        <div className="wb-bubble">
          <blockquote className="wb-scene-quote">
            &bdquo;{person.quote}&ldquo;
          </blockquote>
          <div className="wb-bubble-author">
            <img
              src={person.image}
              alt={person.name}
              className="wb-scene-avatar"
              loading="lazy"
            />
            <div className="wb-bubble-author-info">
              <span className="wb-scene-name">{person.name}</span>
              <span className="wb-scene-role">{person.role}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const WastedBudget = () => {
  const { language } = useLanguage();
  const t = translations[language].wastedBudget;
  const [headerRef, headerVisible] = useScrollAnimation({ threshold: 0.2 });
  const [invoiceRef, invoiceVisible] = useScrollAnimation({ threshold: 0.15 });

  const sectionRef = useRef(null);
  const [printProgress, setPrintProgress] = useState(0);
  const rafRef = useRef(null);

  const handleScroll = useCallback(() => {
    if (!sectionRef.current) return;
    const rect = sectionRef.current.getBoundingClientRect();
    const vh = window.innerHeight;
    const raw = (vh - rect.top) / (vh + rect.height * 0.5);
    setPrintProgress(Math.min(1, Math.max(0, raw)));
  }, []);

  useEffect(() => {
    const onScroll = () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(handleScroll);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    handleScroll();
    return () => {
      window.removeEventListener('scroll', onScroll);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [handleScroll]);

  const invoiceShift = printProgress * 120;

  return (
    <section ref={sectionRef} className="wb-section">
      <div className="wb-inner">
        <div
          ref={headerRef}
          className={`wb-header scroll-fade-in ${headerVisible ? 'is-visible' : ''}`}
        >
          <span className="section-label">{t.label}</span>
          {t.headlines ? (
            <>
              {t.quotesTitle && (
                <h2 className="wb-title">{t.quotesTitle}</h2>
              )}
              {t.quotesSubtitle && (
                <p className="wb-subtitle">{t.quotesSubtitle}</p>
              )}
              <div className="wb-quotes-stack">
                {t.headlines.map((person, i) => (
                  <QuoteCard key={i} person={person} index={i} />
                ))}
              </div>
              {t.headlinesSubtitle && (
                <p className="wb-headlines-subtitle">{t.headlinesSubtitle}</p>
              )}
            </>
          ) : (
            <>
              <h2 className="wb-title">{t.title}</h2>
              <p className="wb-subtitle">{t.subtitle}</p>
            </>
          )}
        </div>

        <div
          ref={invoiceRef}
          className={`wb-invoice-container ${invoiceVisible ? 'wb-invoice-visible' : ''}`}
        >
          <div className="wb-slot">
            <div className="wb-slot-top-surface" />
            <div className="wb-slot-hole" />
            <div className="wb-slot-led" />
          </div>

          <div className="wb-slot-lip" />

          <div
            className="wb-invoice"
            style={{ transform: `translateX(-50%) translateY(${invoiceShift}px)` }}
          >
            <span className="wb-company-label">{t.companyLabel}</span>
            <h3 className="wb-invoice-title">{t.invoiceTitle}</h3>

            <ul className="wb-channel-list">
              {t.channels.map((ch, i) => (
                  <li key={i} className="wb-channel-item">
                    <div className="wb-channel-icon-wrap">
                      <ChannelIcon type={ch.icon} />
                    </div>
                    <p className="wb-channel-info">
                      <span className="wb-channel-name">{ch.name}</span>
                      <span className="wb-channel-amount">{ch.amount}</span>
                    </p>
                  </li>
              ))}
            </ul>

            <div className="wb-divider" />

            <div className="wb-amount-row">
              <span>{t.totalLabel}</span>
              <span className="wb-amount-value">{t.totalValue}</span>
            </div>
            <div className="wb-amount-row">
              <span>{t.perLeadLabel}</span>
              <span className="wb-amount-value wb-amount-danger">{t.perLeadValue}</span>
            </div>

            <div className="wb-result-box">
              <div className="wb-result-heading">
                <span>{t.resultHeading}</span>
                {t.resultValue ? <span className="wb-result-value">{t.resultValue}</span> : null}
              </div>
              {t.progressLabel ? (
                <>
                  <div className="wb-progress-bar">
                    <div className="wb-progress-fill" />
                  </div>
                  <div className="wb-progress-meta">
                    <span>{t.progressLabel}</span>
                    <span className="wb-progress-percent">{t.progressPercent}</span>
                  </div>
                </>
              ) : null}
              <div className="wb-divider" />
              <div className="wb-roi-row">
                <span>{t.roiLabel}</span>
                <span className="wb-roi-value">{t.roiValue}</span>
              </div>
            </div>

            <div className="wb-btn-group">
              <span className="wb-bottom-line">{t.bottomLine}</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default WastedBudget;
